import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '../../api';
import { useDevice } from '@/contexts/DeviceContext';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';

import { useBlockedApps } from '@/hooks/useBlockedApps';
import { useMentorStatus } from '@/hooks/useMentorStatus';
import { useDeviceAppMode } from '@/hooks/useDeviceAppMode';
import { useAppCategories } from '@/hooks/useAppCategories';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useDebounce } from '@/hooks/use-debounce';

import AppCard from '@/components/AppCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, RefreshCw, Store, ShieldX, Apple, Loader2 } from 'lucide-react';
import { ProfileAvatar } from '@/components/ProfileAvatar';

import { App } from '@/types/App';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import PullToRefreshIndicator from '@/components/PullToRefreshIndicator';

// Helper to check if app should be hidden (system apps like webkit, safari)
const isHiddenApp = (name: string): boolean => {
  const lowerName = name.toLowerCase().trim();
  return lowerName === 'webkit' || lowerName === 'safari' || lowerName.includes('webkit');
};

// Transform raw backend app to App type
const transformApp = (app: any, index: number): App => ({
  id: app.id ? String(app.id) : `temp-${app.trackId || index}`,
  name: app.name || 'Unknown App',
  icon: app.iconUrl || '/placeholder.svg',
  description: '',
  version: '',
  appStoreId: String(app.trackId || ''),
  category: app.category || undefined,
  bundleId: app.bundleId,
});

const Index: React.FC = () => {
  const navigate = useNavigate();
  const { selectedDevice } = useDevice();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isSelfManaged } = useMentorStatus();
  const { currentMode, isLoading: isAppModeLoading } = useDeviceAppMode();
  const { allowedApps: whitelistFromConfig, isLoading: isAppCategoriesLoading } = useAppCategories();
  const isWhitelistMode = !isAppModeLoading && currentMode === 'approved-only';
  // Serial
  const { serial, isLoading: isSerialLoading } = useEffectiveSerial();

  // Search state
  const [inputQuery, setInputQuery] = useState('');
  const debouncedQuery = useDebounce(inputQuery, 300);
  const isSearchMode = debouncedQuery.length >= 3;
  const isSearching = inputQuery.length >= 3 && (inputQuery !== debouncedQuery);

  const shouldQuery = !!serial && !isSerialLoading;

  // Paginated query - always run when serial is available (backend handles mode filtering)
  const { results: internalResults, loadMore, status } = usePaginatedQuery(
    (api.apps.list as any).allowedByDeviceFilter,
    shouldQuery ? {
      serial,
      ...(isSearchMode ? { query: debouncedQuery } : {})
    } : "skip",
    { initialNumItems: 50 }
  );

  // Static query - always run in browse mode for explicit whitelist entries
  const staticAllowed = useQuery(
    (api.apps.list as any).allowedByDeviceStatic,
    shouldQuery && !isSearchMode ? { serial } : "skip"
  );

  // Merge results - always merge both sources regardless of detected mode
  const mergedApps = useMemo(() => {
    const safeStatic = (staticAllowed ?? []) as any[];
    const safeInternal = (internalResults ?? []) as any[];

    if (isSearchMode) {
      // Search mode: backend includes statics, just use paginated results
      return safeInternal
        .map((app, i) => transformApp(app, i))
        .filter(app => app.name.trim() !== '' && app.name !== '*' && !isHiddenApp(app.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    // Browse mode: merge static + paginated, deduplicate
    const staticToShow = status === 'Exhausted'
      ? safeStatic
      : safeInternal.length === 0
        ? []
        : safeStatic.filter(app => app.name < safeInternal.at(-1)!.name);

    const combined = [...safeInternal, ...staticToShow];
    const seen = new Set<string>();
    const deduped = combined.filter(app => {
      const key = app.id ? String(app.id) : app.name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return deduped
      .map((app, i) => transformApp(app, i))
      .filter(app => app.name.trim() !== '' && app.name !== '*' && !isHiddenApp(app.name))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [internalResults, staticAllowed, status, isSearchMode]);

  // Split into whitelisted (explicit overrides) vs category apps
  const staticIds = useMemo(() => {
    if (isSearchMode) return new Set<string>();
    return new Set((staticAllowed ?? []).map((a: any) => a.id ? String(a.id) : a.name));
  }, [staticAllowed, isSearchMode]);

  const whitelistedApps = useMemo(() => {
    if (isSearchMode) return [];
    return mergedApps.filter(app => staticIds.has(app.id));
  }, [mergedApps, staticIds, isSearchMode]);

  const categoryApps = useMemo(() => {
    if (isSearchMode) return [];
    return mergedApps.filter(app => !staticIds.has(app.id));
  }, [mergedApps, staticIds, isSearchMode]);

  // Available categories from loaded apps
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    mergedApps.forEach(app => { if (app.category) cats.add(app.category); });
    return Array.from(cats).sort();
  }, [mergedApps]);

  // Loading states - unified, no mode gating
  const isLoading = status === 'LoadingFirstPage' || internalResults === undefined || (!isSearchMode && staticAllowed === undefined);
  
  const isFullyLoaded = isSearchMode ? true : status === 'Exhausted' && staticAllowed !== undefined;

  const hasNextPage = status === 'CanLoadMore';
  const isFetchingNextPage = status === 'LoadingMore';

  const fetchNextPage = useCallback(() => {
    if (status === 'CanLoadMore') loadMore(50);
  }, [loadMore, status]);

  const { blockedApps, hasAppleAppStore, isLoading: isLoadingBlocked, isLoadingConfig } = useBlockedApps();

  // Dynamic category options
  const categoryOptions = useMemo(() => {
    const sorted = [...availableCategories].sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    return [
      { id: 'all', label: 'All Categories' },
      ...sorted.map(cat => ({ id: cat, label: cat }))
    ];
  }, [availableCategories]);

  // Infinite scroll
  const { loadMoreRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    root: scrollContainerRef.current,
  });

  // Pull to refresh (no-op retry since Convex auto-refreshes)
  const { isPulling, isRefreshing, pullProgress, setContainerRef } = usePullToRefresh({
    onRefresh: async () => {}
  });

  const setRef = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setContainerRef(el);
  }, [setContainerRef]);

  // Category matching helper
  const matchesCategory = (appCategory: string | undefined, selectedCategoryId: string): boolean => {
    if (!appCategory) return false;
    if (selectedCategoryId === 'all') return true;
    
    const appCat = appCategory.toLowerCase().trim();
    const selectedCat = selectedCategoryId.toLowerCase().trim();
    
    if (appCat === selectedCat) return true;
    
    const categoryMappings: Record<string, string[]> = {
      'photo & video': ['photo', 'video', 'photography', 'photo-video', 'camera', 'photos & video'],
      'productivity': ['productivity', 'business', 'work'],
      'health & fitness': ['health', 'fitness', 'health-fitness', 'healthcare', 'medical'],
      'social networking': ['social', 'social networking', 'social-networking', 'communication'],
      'utilities': ['utilities', 'utility', 'tools'],
      'lifestyle': ['lifestyle', 'life'],
      'music': ['music', 'audio'],
      'navigation': ['navigation', 'maps', 'travel'],
      'shopping': ['shopping', 'retail', 'ecommerce'],
      'games': ['games', 'game', 'gaming'],
      'education': ['education', 'educational', 'learning'],
      'finance': ['finance', 'financial', 'banking', 'money'],
      'entertainment': ['entertainment', 'media', 'streaming'],
    };
    
    const variations = categoryMappings[selectedCat];
    if (variations) {
      return variations.some(v => appCat.includes(v) || v.includes(appCat));
    }
    
    return appCat.includes(selectedCat) || selectedCat.includes(appCat);
  };

  // Filter by selected category
  const filteredWhitelisted = useMemo(() => {
    if (isSearchMode) return [];
    let apps = whitelistedApps;
    if (selectedCategory !== 'all') {
      apps = apps.filter(app => matchesCategory(app.category, selectedCategory));
    }
    return apps;
  }, [whitelistedApps, selectedCategory, isSearchMode]);

  const filteredCategory = useMemo(() => {
    if (isSearchMode) return [];
    let apps = categoryApps;
    if (selectedCategory !== 'all') {
      apps = apps.filter(app => matchesCategory(app.category, selectedCategory));
    }
    return apps;
  }, [categoryApps, selectedCategory, isSearchMode]);

  // Display apps
  const displayApps = useMemo(() => {
    if (isSearchMode) return mergedApps;
    return [...filteredWhitelisted, ...filteredCategory];
  }, [isSearchMode, mergedApps, filteredWhitelisted, filteredCategory]);

  // Whitelist-only display apps from deviceAppConfig.allowedApps
  const whitelistDisplayApps = useMemo(() => {
    const configApps: App[] = (whitelistFromConfig ?? []).map((app: any, i: number) => ({
      id: app.id || `wl-${i}`,
      name: app.name || 'Unknown App',
      icon: app.iconUrl || '/placeholder.svg',
      description: '',
      version: '',
      appStoreId: String(app.trackId || app.id || ''),
      category: app.category || undefined,
      bundleId: app.bundleId,
    }));
    
    if (isSearchMode) {
      const query = debouncedQuery.toLowerCase();
      return configApps.filter(app => app.name.toLowerCase().includes(query));
    }
    return configApps;
  }, [whitelistFromConfig, isSearchMode, debouncedQuery]);

  // Blocked apps search
  const filteredBlockedApps = useMemo(() => {
    if (!inputQuery.trim()) return blockedApps;
    const query = inputQuery.toLowerCase();
    return blockedApps.filter(app => app.name.toLowerCase().includes(query));
  }, [blockedApps, inputQuery]);

  const handleAppSelect = (app: App) => {
    if (selectedDevice?._id) {
      navigate(`/${selectedDevice._id}/app/${app.appStoreId}`);
    }
  };

  const handleNavigateToUpdates = () => {
    if (selectedDevice?._id) {
      navigate(`/${selectedDevice._id}/updates`);
    } else {
      navigate('/updates');
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
  };

  // Skeleton loading
  const LoadingSkeleton = () => (
    <div className="divide-y divide-border/50" role="status" aria-live="polite" aria-label="Loading apps">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="px-4 py-3 flex items-center gap-4">
          <Skeleton className="w-14 h-14 rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="w-5 h-5 rounded" />
        </div>
      ))}
      <span className="sr-only">Loading apps...</span>
    </div>
  );

  // Loading config state
  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
        <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg border-b border-border/50 safe-area-top">
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="h-7 w-32" />
            </div>
            <Skeleton className="h-11 w-full rounded-xl" />
          </div>
        </header>
        <main className="px-4 pt-4">
          <LoadingSkeleton />
        </main>
      </div>
    );
  }

  // Apple App Store Mode - show blocked apps
  if (hasAppleAppStore) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
        <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] safe-area-top">
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-md">
                  <Apple className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Apple App Store</h1>
              </div>
              <ProfileAvatar />
            </div>
            
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Apple className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Your device uses the Apple App Store</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You can download any app from the App Store, except for the restricted apps listed below.
                  </p>
                </div>
              </div>
            </div>
            
            {blockedApps.length > 0 && (
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" aria-hidden="true" />
                <Input
                  type="search"
                  placeholder="Search blocked apps..."
                  value={inputQuery}
                  onChange={(e) => setInputQuery(e.target.value)}
                  className="pl-10 h-11 bg-muted/50 border-0 rounded-xl placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary/30 text-[15px]"
                  aria-label="Search blocked apps"
                />
              </div>
            )}
          </div>
        </header>

        <main 
          ref={setRef}
          className="relative overflow-auto no-overscroll"
          style={{ height: 'calc(100dvh - 280px)', WebkitOverflowScrolling: 'touch' }}
        >
          <PullToRefreshIndicator isPulling={isPulling} isRefreshing={isRefreshing} pullProgress={pullProgress} />
          
          {isLoadingBlocked ? (
            <LoadingSkeleton />
          ) : filteredBlockedApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mb-4">
                <Apple className="w-8 h-8 text-green-600" aria-hidden="true" />
              </div>
              <p className="text-foreground font-semibold text-lg">
                {inputQuery ? 'No blocked apps match your search' : 'No apps are restricted'}
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                {inputQuery ? 'Try a different search term' : 'You have full access to all apps on the Apple App Store.'}
              </p>
            </div>
          ) : (
            <div className="px-4 py-2">
              <div className="flex items-center gap-2 mb-3">
                <ShieldX className="w-4 h-4 text-destructive" />
                <p className="text-sm font-medium text-muted-foreground">
                  Restricted Apps ({filteredBlockedApps.length})
                </p>
              </div>
              
              <div className="bg-card rounded-xl border border-border/50 divide-y divide-border/50 overflow-hidden">
                {filteredBlockedApps.map((app) => (
                  <div key={app.id} className="px-4 py-3 flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <img 
                        src={app.icon} 
                        alt={`${app.name} icon`}
                        className="w-12 h-12 rounded-xl object-cover bg-muted"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder.svg'; }}
                      />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-destructive flex items-center justify-center">
                        <ShieldX className="w-3 h-3 text-destructive-foreground" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{app.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">Source: {app.source}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Approved Only (Whitelist) Mode - show only approved apps in a flat list
  if (isWhitelistMode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
        <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] safe-area-top">
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                  <Store className="w-5 h-5 text-primary-foreground" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Approved Apps</h1>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-primary font-semibold hover:bg-primary/10 active:bg-primary/20 active:scale-[0.97] gap-1.5 transition-all shrink-0"
                  onClick={handleNavigateToUpdates}
                  aria-label="View app updates"
                >
                  <RefreshCw className="w-4 h-4" />
                  Updates
                </Button>
                <ProfileAvatar />
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Store className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Approved Only Mode</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isSelfManaged ? (
                      <>
                        Only your approved apps are shown.{' '}
                        <Link 
                          to={`/${selectedDevice?._id}/settings/app-management/whitelist?add=true`}
                          className="text-primary font-medium hover:underline"
                        >
                          Add an app
                        </Link>
                      </>
                    ) : (
                      'Only your approved apps are shown. To add an app, request it in the Requests section.'
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" aria-hidden="true" />
              <Input
                type="search"
                placeholder="Search approved apps..."
                value={inputQuery}
                onChange={(e) => setInputQuery(e.target.value)}
                className="pl-10 h-11 bg-muted/50 border-0 rounded-xl placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary/30 text-[15px]"
                aria-label="Search approved apps"
              />
              {isSearching && (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </header>

        <main 
          ref={setRef}
          className="relative overflow-auto no-overscroll pb-20"
          style={{ height: 'calc(100dvh - 140px)', WebkitOverflowScrolling: 'touch' }}
        >
          <PullToRefreshIndicator isPulling={isPulling} isRefreshing={isRefreshing} pullProgress={pullProgress} />
          
          {isLoading ? (
            <div className="px-4 py-4">
              <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading apps...</span>
              </div>
              <LoadingSkeleton />
            </div>
          ) : whitelistDisplayApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Store className="w-7 h-7 text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="text-foreground font-semibold text-lg">
                {isSearchMode ? 'No apps found' : 'No approved apps yet'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {isSearchMode 
                  ? 'Try a different search term' 
                  : isSelfManaged 
                    ? 'Add apps from Settings → App Management' 
                    : 'Request apps in the Requests section'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
                <p className="text-xs text-muted-foreground">
                  {isSearchMode 
                    ? `${whitelistDisplayApps.length} results for "${debouncedQuery}"`
                    : `${whitelistDisplayApps.length} approved apps`
                  }
                </p>
              </div>
              <div className="divide-y divide-border/50">
                {whitelistDisplayApps.map((app: App) => (
                  <AppCard key={app.id} app={app} onAppSelect={handleAppSelect} />
                ))}
              </div>
              {hasNextPage && (
                <div ref={loadMoreRef} className="py-6 flex justify-center">
                  {isFetchingNextPage && (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    );
  }

  const isLoadingBrowse = !isSearchMode && !isFullyLoaded && isLoading;
  const isLoadingMore = isFetchingNextPage || hasNextPage;

  // Regular App Store Mode
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
      {import.meta.env.DEV && (
        <div className="fixed left-3 bottom-24 z-50 rounded-lg border border-border bg-background/95 backdrop-blur px-3 py-2 text-xs text-foreground shadow-sm">
          <div className="font-medium">Apps debug</div>
          <div className="text-muted-foreground">
            total: {displayApps.length} • isSearchMode: {String(isSearchMode)}
          </div>
          <div className="text-muted-foreground">
            status: {String(status)} • serial: {String(serial ?? 'n/a')}
          </div>
          <div className="text-muted-foreground">
            isFullyLoaded: {String(isFullyLoaded)} • loadingMore: {String(isLoadingMore)}
          </div>
        </div>
      )}
      
      <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] safe-area-top">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                <Store className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">App Store</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-primary font-semibold hover:bg-primary/10 active:bg-primary/20 active:scale-[0.97] gap-1.5 transition-all shrink-0"
                onClick={handleNavigateToUpdates}
                aria-label="View app updates"
              >
                <RefreshCw className="w-4 h-4" />
                Updates
              </Button>
              <ProfileAvatar />
            </div>
          </div>
          
          <p className="text-[13px] text-muted-foreground mb-4">
            {isSelfManaged ? (
              <>
                To add an app,{' '}
                <Link 
                  to={`/${selectedDevice?._id}/settings/app-management/whitelist?add=true`}
                  className="text-primary font-medium hover:underline"
                >
                  press here
                </Link>
              </>
            ) : (
              'To add an app, request it in the Requests section'
            )}
          </p>
          
          <div className="relative mb-3">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" aria-hidden="true" />
            <Input
              type="search"
              placeholder="Search apps..."
              value={inputQuery}
              onChange={(e) => setInputQuery(e.target.value)}
              className="pl-10 h-11 bg-muted/50 border-0 rounded-xl placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary/30 text-[15px]"
              aria-label="Search apps"
            />
            {isSearching && (
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          
          {!isSearchMode && (
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="h-11 bg-muted/50 border-0 rounded-xl focus:ring-1 focus:ring-primary/30">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      <main 
        ref={setRef}
        className="relative overflow-auto no-overscroll pb-20"
        style={{ height: 'calc(100dvh - 140px)', WebkitOverflowScrolling: 'touch' }}
      >
        <PullToRefreshIndicator isPulling={isPulling} isRefreshing={isRefreshing} pullProgress={pullProgress} />
        
        {(isLoading || isLoadingBrowse) ? (
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading apps...</span>
            </div>
            <LoadingSkeleton />
          </div>
        ) : displayApps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Search className="w-7 h-7 text-muted-foreground" aria-hidden="true" />
            </div>
            <p className="text-foreground font-semibold text-lg">
              {isSearchMode ? 'No apps found' : 'No apps available'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {isSearchMode ? 'Try a different search term' : 'No apps match the selected category'}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
              <p className="text-xs text-muted-foreground">
                {isSearchMode 
                  ? `${displayApps.length} results for "${debouncedQuery}"`
                  : `${displayApps.length} apps${selectedCategory !== 'all' ? ` in ${selectedCategory}` : ''}`
                }
              </p>
              {isLoadingMore && !isSearchMode && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading more...
                </div>
              )}
            </div>

            {isSearchMode && (
              <div className="divide-y divide-border/50">
                {displayApps.map((app: App) => (
                  <AppCard key={app.id} app={app} onAppSelect={handleAppSelect} />
                ))}
              </div>
            )}

            {!isSearchMode && (
              <>
                {filteredWhitelisted.length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-4 pt-4 pb-2">
                      Whitelisted
                    </p>
                    <div className="divide-y divide-border/50">
                      {filteredWhitelisted.map((app: App) => (
                        <AppCard key={app.id} app={app} onAppSelect={handleAppSelect} />
                      ))}
                    </div>
                  </>
                )}

                {filteredCategory.length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-4 pt-4 pb-2">
                      All Other
                    </p>
                    <div className="divide-y divide-border/50">
                      {filteredCategory.map((app: App) => (
                        <AppCard key={app.id} app={app} onAppSelect={handleAppSelect} />
                      ))}
                    </div>
                  </>
                )}

                {hasNextPage && (
                  <div ref={loadMoreRef} className="py-6 flex justify-center">
                    {isFetchingNextPage && (
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Index;
